/**
 * State en acties voor de e-mailverificatiebanner (cooldown, resend, focus-reload).
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/features/auth/useAuth';
import { getAuthErrorKey } from '@/services/authService';
import { sendUserEmailVerification } from '@/services/authEmailVerification';
import {
  persistBannerDismissed,
  readBannerDismissed,
  shouldShowEmailVerificationBanner,
} from '@/features/auth/utils/emailVerificationBanner';
import { useT } from '@/i18n/useT';

const RESEND_COOLDOWN_MS = 60_000;

export const useEmailVerificationBanner = () => {
  const { user, refreshUser } = useAuth();
  const t = useT();
  const [dismissed, setDismissed] = useState(() => readBannerDismissed());
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [errorKey, setErrorKey] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const visible = shouldShowEmailVerificationBanner(user, dismissed);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }
    const onFocus = () => {
      refreshUser?.().catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [visible, refreshUser]);

  useEffect(() => {
    if (cooldownUntil <= Date.now()) {
      return undefined;
    }
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  const onDismiss = useCallback(() => {
    persistBannerDismissed();
    setDismissed(true);
  }, []);

  const onResend = useCallback(async () => {
    if (!user || cooldownUntil > Date.now()) {
      return;
    }
    setErrorKey(null);
    try {
      await sendUserEmailVerification(user);
      setCooldownUntil(Date.now() + RESEND_COOLDOWN_MS);
      setNow(Date.now());
    } catch (err) {
      setErrorKey(getAuthErrorKey(err));
    }
  }, [user, cooldownUntil]);

  const resendDisabled = cooldownUntil > now;
  const errorMessage = errorKey ? t(errorKey) : null;

  return {
    visible,
    onDismiss,
    onResend,
    resendDisabled,
    errorMessage,
  };
};
